import dynamic from "next/dynamic";
import Head from "next/head";

import { useMemo } from "react";

import { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import useActivityTracker from "hooks/useActivityTracker";
import { useEffectOnce } from "hooks/useEffectOnce";
import shortSecurity from "utils/shortSecurity";

import EarningsAlertWidget from "../EarningsAlertWidget";

import ConsensusPriceTarget from "./ConsensusPriceTarget";
import ConsensusRatings from "./ConsensusRatings";
import Estimates from "./Estimates";
import NextEarning from "./NextEarning";
import PriceTarget from "./PriceTarget";

type EntityPageEstimatesCombinedProps = { entity: Entity };

const EntityRecentEvent = dynamic(
  () => import("components/EntityRecentEvent"),
  {
    ssr: false,
  }
);

const EntityPageEstimatesCombined = ({
  entity,
}: EntityPageEstimatesCombinedProps) => {
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);

  const { trackActivity } = useActivityTracker();
  useEffectOnce(
    (ack) => {
      if (!entity) return;

      ack();
      trackActivity("consensus.view", entity);
    },
    [entity]
  );

  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);

  const title = `${pageTitleName} - Estimates`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
      </Head>

      <div className="flex flex-col md:flex-row md:space-x-[30px]">
        <div className="flex-1">
          <CustomCard
            className={twMerge(
              "bg-white rounded-lg shadow-sm",
              "mb-0 md:mb-[30px]",
              "min-h-[max(calc(100vh-500px),720px)]",
              "space-y-[30px]",
              "px-[15px] md:px-[30px]"
            )}
          >
            <Estimates entity={entity} showEarningMatrix />
            <PriceTarget entity={entity} />
          </CustomCard>
        </div>
        <div className="md:w-[300px] space-y-[30px] px-[15px] md:px-0">
          <NextEarning entity={entity} />
          <ConsensusPriceTarget entity={entity} />
          <ConsensusRatings entity={entity} />
          <EarningsAlertWidget
            containerClassName={"shadow-[0_2px_15px_rgba(0,0,0,0.15)]"}
            headerClassName="text-center md:text-left"
          />
          <EntityRecentEvent
            widget
            containerClassName={"shadow-[0_2px_15px_rgba(0,0,0,0.15)]"}
            headerClassName="text-center md:text-left"
          />
        </div>
      </div>
    </>
  );
};

export default EntityPageEstimatesCombined;
